import React, { useCallback, useState } from 'react';

export interface Step {
    id: string;
    label: string;
    description?: string;
    backLabel?: string;
}

export interface StepperProps {
    steps: Step[];
    currentStep: number;
    onStepChange?: (stepIndex: number) => void;
    className?: string;
}

export const Stepper: React.FC<StepperProps> = ({ steps, currentStep, className = '' }) => {
    const getProgressPercentage = () => {
        if (steps.length === 0) return 0;
        return Math.round(((currentStep + 1) / steps.length) * 100);
    };

    return (
        <div className={`flex flex-col items-center gap-2 ${className}`}>
            <div className="flex items-center justify-start gap-4 w-full">
                {/* Progress bar */}
                <div className="flex-1 bg-gray-100 rounded-full h-2.5 relative">
                    <div
                        className="bg-[#feca42] h-2.5 rounded-full transition-all duration-300 ease-in-out"
                        style={{ width: `${getProgressPercentage()}%` }}
                    />
                </div>

                {/* Percentage */}
                <div className="text-sm font-medium text-gray-800">{getProgressPercentage()}%</div>
            </div>
        </div>
    );
};

// Hook to manage stepper state
export const useStepper = (initialStep: number = 0) => {
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [stepData, setStepData] = useState<Record<string, any>>({});

    const goToStep = useCallback((stepIndex: number) => {
        setCurrentStep(stepIndex);
    }, []);

    const goNext = useCallback(() => {
        setCurrentStep((prev: number) => prev + 1);
    }, []);

    const goBack = useCallback(() => {
        setCurrentStep((prev: number) => Math.max(0, prev - 1));
    }, []);

    const updateStepData = useCallback((stepId: string, data: any) => {
        setStepData((prev: Record<string, any>) => ({
            ...prev,
            [stepId]: data
        }));
    }, []);

    const getStepData = (stepId: string) => {
        return stepData[stepId];
    };

    const resetStepper = useCallback(() => {
        setCurrentStep(0);
        setStepData({});
    }, []);

    return {
        currentStep,
        stepData,
        goToStep,
        goNext,
        goBack,
        updateStepData,
        getStepData,
        resetStepper
    };
};

export default Stepper;
